// src/graph-view.js
//
// The framework-agnostic orchestrator — no React, no Alpine, not even a
// dependency on which of the three backends `driver` actually is. A React
// binding wraps this in a hook; an Alpine template calls it directly from
// `x-init`/`x-effect`. Both see the same object.

/**
 * @param {Array<{id: string}>} nodes
 * @param {Array<{source: string, target: string}>} edges
 * @throws {Error} on a duplicate node id or an edge naming a node that is not there —
 *         caught here, at the boundary, instead of surfacing as a backend-specific
 *         "drew nothing, no error" a few layers down.
 */
function validate(nodes, edges) {
    const seen = new Set();

    for (const node of nodes) {
        if (node.id === undefined || node.id === null || node.id === '') {
            throw new Error('every node needs a non-empty id');
        }

        if (seen.has(node.id)) {
            throw new Error(`duplicate node id "${node.id}"`);
        }

        seen.add(node.id);
    }

    for (const edge of edges) {
        if (!seen.has(edge.source)) {
            throw new Error(`edge ${JSON.stringify(edge)} names a source "${edge.source}" that is not a node`);
        }

        if (!seen.has(edge.target)) {
            throw new Error(`edge ${JSON.stringify(edge)} names a target "${edge.target}" that is not a node`);
        }
    }
}

export class GraphView {
    /** @param {import('./graph-driver.js').GraphDriver} driver */
    constructor(driver) {
        this.driver = driver;
        this._container = null;
        this._resize_observer = null;
    }

    /**
     * @param {HTMLElement} container
     * @param {object} [options] passed straight through to the driver — its
     *        shape is backend-specific on purpose, an escape hatch rather
     *        than a lowest-common-denominator options object nobody can use.
     */
    mount(container, options = {}) {
        this.driver.mount(container, options);
        this._container = container;

        // Not every environment has one (jsdom, notably) — resize just
        // becomes something the caller has to invoke by hand there, which
        // is exactly right for a test.
        if (typeof ResizeObserver !== 'undefined') {
            this._resize_observer = new ResizeObserver(() => this.driver.resize());
            this._resize_observer.observe(container);
        }

        return this;
    }

    /**
     * @param {Array<{id: string, label?: string, group?: string}>} nodes
     * @param {Array<{source: string, target: string, label?: string}>} edges
     */
    setData(nodes, edges) {
        validate(nodes, edges);
        this.driver.setData(nodes, edges);

        return this;
    }

    /** @param {'click'|'hover'|'select'} event @param {(payload: object) => void} callback */
    on(event, callback) {
        this.driver.on(event, callback);

        return this;
    }

    fit() {
        this.driver.fit();

        return this;
    }

    destroy() {
        if (this._resize_observer !== null) {
            this._resize_observer.disconnect();
            this._resize_observer = null;
        }

        this.driver.destroy();
        this._container = null;
    }
}
